import { useContext, useCallback, useRef } from 'react';
import { CsrfTokenContext } from '~/core/contexts/csrf-token';
import { throwIfProtectedApiResponseStartsRecovery } from '~/core/firebase/client-auth-recovery';
import logger from '~/core/logger';

/**
 * @description Retrieves the current CSRF token in the page.
 * For backward compatibility, returns just the token string.
 */
export function useCsrfToken(): Maybe<string> {
  const { token } = useContext(CsrfTokenContext);
  return token;
}

/**
 * @description Hook that provides CSRF token and refresh functionality.
 * Use this when you need to handle CSRF token expiration gracefully.
 */
export function useCsrf() {
  const { token, setToken } = useContext(CsrfTokenContext);

  // Track in-flight refresh to prevent race conditions when multiple
  // requests fail simultaneously and all try to refresh the token
  const refreshInProgressRef = useRef<Promise<string | null> | null>(null);

  /**
   * Refresh the CSRF token by calling the server endpoint.
   * Updates the context so all components get the fresh token.
   * Returns the new token on success, or null on failure.
   *
   * If a refresh is already in progress, returns that promise instead
   * of starting a new refresh (prevents race conditions).
   */
  const refreshToken = useCallback(async (): Promise<string | null> => {
    // Return existing promise if refresh already in progress
    if (refreshInProgressRef.current) {
      logger.debug('CSRF refresh already in progress, waiting for result');
      return refreshInProgressRef.current;
    }

    // Start new refresh and track the promise
    refreshInProgressRef.current = (async () => {
      try {
        logger.info('Refreshing CSRF token');

        const response = await fetch('/api/session/refresh-csrf', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          await throwIfProtectedApiResponseStartsRecovery(
            response,
            'useCsrf.refreshToken',
          );
          logger.warn(
            { status: response.status },
            'Failed to refresh CSRF token',
          );
          return null;
        }

        const data = (await response.json()) as { csrfToken: string };
        const newToken = data.csrfToken;

        setToken(newToken);
        logger.info('CSRF token refreshed successfully');

        return newToken;
      } catch (err) {
        if (
          err instanceof Error &&
          err.name === 'SessionRecoveryStartedError'
        ) {
          throw err;
        }

        logger.error({ err }, 'Error refreshing CSRF token');
        return null;
      } finally {
        // Clear the ref so future refreshes can proceed
        refreshInProgressRef.current = null;
      }
    })();

    return refreshInProgressRef.current;
  }, [setToken]);

  return { token, refreshToken };
}
